import React, { useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone-uploader';
import Select from 'react-select';
import { ReactComponent as Add } from '../../../../components/assets/svg/uploadFile.svg';
import 'react-dropzone-uploader/dist/styles.css';
import { ButtonTable } from '../../../global/Styles/buttons';
import { checkAPI_URL } from '../../../../utils/environment';
import { ErrorToast, OkToast } from '../../Toast';
import { useApiClient } from '../../../../clients/apiAuth';

const mappingFields = [
  { key: 'reporte', apiKey: 'REPORTE', label: 'No. de Reporte' },
  { key: 'valorTarifa', apiKey: 'VALOR_TARIFA', label: 'Valor Tarifa' },
  { key: 'fraccion', apiKey: 'FRACCION', label: 'Fraccion' },
  { key: 'total', apiKey: 'TOTAL', label: 'Total' },
];

const SubirConciliacion = (props) => {
  const { setUploadFile, handleRefresh } = props.actions;
  const { post } = useApiClient();
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedSheetIndex, setSelectedSheetIndex] = useState(0);
  const [mapping, setMapping] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const acceptedFileTypes = ['.xlsx', '.xls'];

  useEffect(() => {
    if (uploadResult?.sheets?.length) {
      setSelectedSheetIndex(0);
      setMapping({});
    }
  }, [uploadResult]);

  const sheetOptions = useMemo(() => {
    if (!uploadResult?.sheets?.length) {
      return [];
    }
    return uploadResult.sheets.map((sheet, index) => ({
      label: sheet.name || `Hoja ${index + 1}`,
      value: index,
    }));
  }, [uploadResult]);

  const selectedSheet = uploadResult?.sheets?.[selectedSheetIndex];

  const columnOptions = useMemo(() => {
    if (!selectedSheet?.headers?.length) {
      return [];
    }
    return selectedSheet.headers.map((header, index) => ({
      label: `Col ${index + 1} - ${header || 'Sin encabezado'}`,
      value: index,
      header: header || '',
    }));
  }, [selectedSheet]);

  const styleUpload = () => {
    return (
      <label
        style={{
          color: '#fff',
          cursor: 'pointer',
          padding: 10,
          borderRadius: 3,
          zIndex: -10,
        }}>
        <Add className="h-12 w-12" />
      </label>
    );
  };

  const handleSubmitFile = (files, allFiles) => {
    allFiles.forEach((file) => file.remove());

    if (!files.length) {
      return;
    }

    const responseText = files[0]?.xhr?.response;
    try {
      const json = JSON.parse(responseText);
      if (json?.success) {
        setUploadResult(json);
        OkToast({ message: json.message || 'Archivo cargado' });
        handleRefresh?.();
        return;
      }
      ErrorToast('Error', json?.message || 'No se pudo cargar el archivo.');
    } catch (error) {
      ErrorToast('Error', 'Respuesta invalida del servidor.');
    }
  };

  const getUploadParams = ({ file }) => {
    const body = new FormData();
    body.append('file', file);
    return { url: `${checkAPI_URL()}/importarTakeOff`, body };
  };

  const handleChangeStatus = ({ xhr }, status) => {
    if (xhr && status === 'error_upload') {
      ErrorToast('Error', 'Se ha producido un error al cargar el archivo.');
    }
  };

  const handleSheetChange = (option) => {
    if (!option) {
      return;
    }
    setSelectedSheetIndex(option.value);
    setMapping({});
  };

  const handleMappingChange = (fieldKey, option) => {
    setMapping((prev) => ({
      ...prev,
      [fieldKey]: option
        ? {
            index: option.value,
            header: option.header,
          }
        : null,
    }));
  };

  const indexToColumnLabel = (index) => {
    let result = '';
    let current = index;
    while (current >= 0) {
      result = String.fromCharCode((current % 26) + 65) + result;
      current = Math.floor(current / 26) - 1;
    }
    return result;
  };

  const canProcess =
    !!uploadResult?.token &&
    !!selectedSheet?.name &&
    mappingFields.every((field) => mapping[field.key]);

  const handleProcess = async () => {
    if (!canProcess || isProcessing) {
      return;
    }

    const mappingPayload = mappingFields.reduce((acc, field) => {
      const selected = mapping[field.key];
      if (selected) {
        acc[field.apiKey] = indexToColumnLabel(selected.index);
      }
      return acc;
    }, {});

    try {
      setIsProcessing(true);
      const response = await post('/procesarTakeOff', {
        token: uploadResult.token,
        sheet: selectedSheet.name,
        mapping: mappingPayload,
      });

      if (response?.success || String(response?.status || '').startsWith('2')) {
        OkToast({ message: response?.message || 'Proceso iniciado' });
        handleRefresh?.();
        setUploadFile(false);
        return;
      }
      ErrorToast('Error', response?.message || 'No se pudo procesar el archivo.');
    } catch (error) {
      ErrorToast('Error', 'No se pudo procesar el archivo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const mappingSummary = mappingFields
    .map((field) => ({
      label: field.label,
      value: mapping[field.key],
    }))
    .filter((item) => item.value);

  return (
    <div
      className="min-w-screen animated fadeIn faster outline-none  focus:outline-none  fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-y-scroll bg-cover bg-center bg-no-repeat"
      id="modal-id">
      <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
      <div className="relative mx-auto my-auto w-full max-w-3xl rounded-xl bg-white p-5 shadow-lg">
        <div className="flex-auto justify-center p-5 text-left">
          <h2 className="py-2 text-xl">Importar takeoff</h2>
          <p className="text-sm text-gray-700">Sube el archivo en formato XLSX y define el mapeo de columnas.</p>

          <div className="mt-4">
            <Dropzone
              getUploadParams={getUploadParams}
              onChangeStatus={handleChangeStatus}
              onSubmit={handleSubmitFile}
              maxFiles={1}
              multiple={false}
              canCancel={true}
              accept={acceptedFileTypes.join(',')}
              inputContent={styleUpload}
              styles={{
                dropzone: {
                  minHeight: 180,
                  maxHeight: 230,
                  overflow: 'hidden',
                  opacity: 0.9,
                },
                dropzoneActive: {
                  borderColor: 'green',
                  height: 30,
                },
              }}
            />
          </div>

          {uploadResult ? (
            <div className="mt-4 rounded-lg border border-gray-200 p-4">
              <div className="text-sm text-gray-700">
                <p>
                  <span className="font-semibold">Archivo:</span> {uploadResult?.file?.name || 'Archivo cargado'}
                </p>
                <p>
                  <span className="font-semibold">Token:</span> {uploadResult?.token || 'N/A'}
                </p>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-800">Hoja</p>
                <Select
                  options={sheetOptions}
                  value={sheetOptions.find((option) => option.value === selectedSheetIndex)}
                  onChange={handleSheetChange}
                  className="mt-2"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {mappingFields.map((field) => (
                  <div key={field.key}>
                    <p className="text-sm text-gray-800">{field.label}</p>
                    <Select
                      options={columnOptions}
                      value={
                        mapping[field.key]
                          ? columnOptions.find((option) => option.value === mapping[field.key].index)
                          : null
                      }
                      onChange={(option) => handleMappingChange(field.key, option)}
                      className="mt-2"
                      isDisabled={!columnOptions.length}
                      isClearable
                    />
                  </div>
                ))}
              </div>

              {mappingSummary.length > 0 ? (
                <div className="mt-4 rounded-md bg-gray-50 p-3 text-xs text-gray-600">
                  <p className="font-semibold text-gray-700">Mapeo seleccionado</p>
                  <ul className="mt-2 list-disc pl-4">
                    {mappingSummary.map((item, index) => (
                      <li key={index}>
                        {item.label}: {indexToColumnLabel(item.value.index)} {item.value.header ? `- ${item.value.header}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="mx-8 mb-2 flex flex-wrap justify-center gap-3 text-center">
          <ButtonTable onClick={() => setUploadFile(false)}>
            Cerrar
          </ButtonTable>
          <ButtonTable
            onClick={handleProcess}
            disabled={!canProcess || isProcessing}
            className={!canProcess || isProcessing ? 'cursor-not-allowed opacity-60' : ''}>
            {isProcessing ? 'Procesando...' : 'Procesar'}
          </ButtonTable>
        </div>
      </div>
    </div>
  );
};

export default SubirConciliacion;
